import { addLocalDays, daysBetweenLocal, startOfLocalWeek } from '@cadencia/domain';

/**
 * Fechas del CALENDARIO LOCAL del usuario (YYYY-MM-DD), no UTC: "el día 12"
 * debe significar lo mismo en Android y en Windows (docs/DATA_MODEL.md §1.2).
 *
 * La ARITMETICA vive en `@cadencia/domain` (`units/local-date.ts`) y aquí solo
 * se reexporta con los nombres de siempre. Había dos implementaciones de sumar
 * días, una aquí y otra en el dominio, y dos copias de la misma regla acaban
 * divergiendo sin que nada avise. La del dominio además opera en UTC por
 * dentro, así que el cambio de hora no la afecta.
 *
 * Lo que se queda aquí es lo que necesita un `Date` de verdad o el idioma del
 * usuario: convertir desde el reloj y formatear. El dominio no hace ninguna de
 * las dos cosas (ADR-007).
 */
export type LocalDateString = string;

export function toLocalDate(date: Date): LocalDateString {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

export function todayLocal(now: Date = new Date()): LocalDateString {
  return toLocalDate(now);
}

/**
 * Día del CALENDARIO LOCAL al que pertenece un INSTANTE.
 *
 * Es la única frontera autorizada entre las dos formas de tiempo que maneja la
 * aplicación, y existe porque mezclarlas es un fallo que ya ha vuelto cuatro
 * veces (R-28, y BUG-001 de `docs/AUDITORIA_2026-09-06.md`).
 *
 * Los sellos (`performedAt`, `decidedAt`, `createdAt`…) los escribe `nowIso()`,
 * que es `new Date().toISOString()`: SIEMPRE UTC, siempre terminados en `Z`.
 * Los días (`todayLocal()`, `startOfWeek()`, `scheduledDate`, `mealDate`…) son
 * etiquetas del calendario del usuario. Recortar los diez primeros caracteres
 * de un sello UTC NO da su día local: da su día UTC, y en España los dos son
 * distintos entre la medianoche y el desfase del huso —hasta las 02:00 en
 * verano—. Una serie de las 00:30 se contaba en el día anterior y, si era
 * lunes, en la SEMANA anterior: el volumen de la semana, el deload y la
 * elección de fase decidían con ella en el sitio equivocado.
 *
 * Falla solo esas dos horas, así que por la mañana no se reproduce y no da
 * ningún error: por eso hace falta una función con nombre y no un `slice`.
 *
 * Para la ARITMÉTICA entre días locales están `addDays`, `startOfWeek` y
 * `daysBetween`, que operan en UTC por dentro a propósito. Esto es lo otro: la
 * ENTRADA desde un instante.
 */
export function diaLocalDe(iso: string): LocalDateString {
  const instante = new Date(iso);
  if (Number.isNaN(instante.getTime())) {
    throw new Error(`Instante no valido: ${iso}`);
  }
  return toLocalDate(instante);
}

export function parseLocalDate(value: LocalDateString): Date {
  const [year, month, day] = value.split('-').map(Number);
  return new Date(year ?? 1970, (month ?? 1) - 1, day ?? 1);
}

export function addDays(value: LocalDateString, days: number): LocalDateString {
  return addLocalDays(value, days);
}

/** Lunes de la semana a la que pertenece la fecha. */
export function startOfWeek(value: LocalDateString): LocalDateString {
  return startOfLocalWeek(value);
}

export function daysBetween(from: LocalDateString, to: LocalDateString): number {
  return daysBetweenLocal(from, to);
}

const FORMATTER_LONG = new Intl.DateTimeFormat('es-ES', {
  weekday: 'long',
  day: 'numeric',
  month: 'long',
});

const FORMATTER_SHORT = new Intl.DateTimeFormat('es-ES', { weekday: 'short', day: 'numeric' });

export function formatLongDate(value: LocalDateString): string {
  return FORMATTER_LONG.format(parseLocalDate(value));
}

export function formatShortDate(value: LocalDateString): string {
  return FORMATTER_SHORT.format(parseLocalDate(value));
}

export function formatDuration(seconds: number): string {
  const minutes = Math.floor(seconds / 60);
  const rest = Math.floor(seconds % 60);
  return `${minutes}:${String(rest).padStart(2, '0')}`;
}
